import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldWarning, CircleNotch, Copy, Lock } from "@phosphor-icons/react";

const CODE_INPUT_CLASS =
  "w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-lg tracking-[0.4em] font-mono text-[#0A0A0A] outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20";

export default function Security() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null); // {qr, secret, backup_codes}
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedCodes, setSavedCodes] = useState(false);

  const isGoogle = user?.auth_provider === "google";

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/2fa/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const beginSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/setup");
      setSetupData(data);
      setSavedCodes(false);
      setCode("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/2fa/enable", { code });
      toast.success("Two-factor authentication is now active.");
      setSetupData(null);
      setCode("");
      await loadStatus();
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) { console.error("Failed to refresh user after enabling 2FA", e); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/2fa/disable", { code: disableCode });
      toast.success("Two-factor authentication disabled.");
      setDisableCode("");
      await loadStatus();
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) { console.error("Failed to refresh user after disabling 2FA", e); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(setupData.backup_codes.join("\n"));
    toast.success("Backup codes copied.");
  };

  const enabled = status?.twofa_enabled;

  return (
    <Layout>
      <div className="mx-auto max-w-2xl" data-testid="security-page">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black tracking-tighter text-[#0A0A0A]">Security</h1>
          <p className="mt-2 text-base text-neutral-600">
            Protect your account with two-factor authentication (2FA).
          </p>
        </div>

        {isGoogle && (
          <div className="mb-6 flex items-start gap-3 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="security-google-note">
            <ShieldWarning size={18} weight="fill" className="mt-0.5 shrink-0" />
            <span>You sign in with Google. App-level 2FA applies to email/password accounts — manage 2FA through your Google account.</span>
          </div>
        )}

        {/* Status card */}
        <div className="rounded-sm border border-neutral-200 bg-white p-6" data-testid="twofa-status-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {enabled ? (
                <ShieldCheck size={28} weight="fill" className="text-[#059669]" />
              ) : (
                <ShieldWarning size={28} weight="fill" className="text-neutral-400" />
              )}
              <div>
                <p className="font-heading text-lg font-bold text-[#0A0A0A]">
                  Authenticator app
                </p>
                <p className="text-sm text-neutral-600" data-testid="twofa-status-text">
                  {status === null ? "Checking…" : enabled ? "Enabled" : "Not enabled"}
                  {enabled && ` · ${status.backup_codes_remaining} backup codes left`}
                </p>
              </div>
            </div>
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                enabled ? "bg-[#059669]/10 text-[#059669]" : "bg-neutral-100 text-neutral-500"
              }`}
            >
              {enabled ? "On" : "Off"}
            </span>
          </div>
        </div>

        {/* Enable flow */}
        {!enabled && !setupData && (
          <button
            data-testid="twofa-begin-setup"
            onClick={beginSetup}
            disabled={busy}
            className="mt-6 flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-60"
          >
            {busy && <CircleNotch size={18} className="animate-spin" />}
            <Lock size={18} weight="bold" /> Set up two-factor authentication
          </button>
        )}

        {!enabled && setupData && (
          <div className="mt-6 space-y-6 rounded-sm border border-neutral-200 bg-white p-6" data-testid="twofa-setup-panel">
            <div>
              <p className="font-heading text-lg font-bold text-[#0A0A0A]">1. Scan this QR code</p>
              <p className="mt-1 text-sm text-neutral-600">
                Use Google Authenticator, Authy, or any TOTP app.
              </p>
              <img src={setupData.qr} alt="2FA QR code" className="mt-4 h-44 w-44 rounded-sm border border-neutral-200" data-testid="twofa-qr" />
              <p className="mt-2 text-xs text-neutral-500">
                Can't scan? Enter this key: <span className="font-mono text-[#0A0A0A]" data-testid="twofa-secret">{setupData.secret}</span>
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <p className="font-heading text-lg font-bold text-[#0A0A0A]">2. Save your backup codes</p>
                <button onClick={copyCodes} className="flex items-center gap-1 text-sm font-semibold text-[#0055FF] hover:underline" data-testid="twofa-copy-codes">
                  <Copy size={15} /> Copy
                </button>
              </div>
              <p className="mt-1 text-sm text-neutral-600">Each code works once if you lose your device. Store them safely — they won't be shown again.</p>
              <div className="mt-3 grid grid-cols-2 gap-2 rounded-sm bg-neutral-50 p-4" data-testid="twofa-backup-codes">
                {setupData.backup_codes.map((c) => (
                  <span key={c} className="font-mono text-sm text-[#0A0A0A]">{c}</span>
                ))}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={savedCodes} onChange={(e) => setSavedCodes(e.target.checked)} data-testid="twofa-saved-codes-check" />
                I've saved these backup codes.
              </label>
            </div>

            <form onSubmit={confirmEnable}>
              <p className="font-heading text-lg font-bold text-[#0A0A0A]">3. Enter the 6-digit code</p>
              <input
                data-testid="twofa-verify-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="000000"
                className={`mt-3 ${CODE_INPUT_CLASS}`}
              />
              <div className="mt-4 flex gap-3">
                <button
                  type="submit"
                  data-testid="twofa-confirm-enable"
                  disabled={busy || code.length !== 6 || !savedCodes}
                  className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-60"
                >
                  {busy && <CircleNotch size={18} className="animate-spin" />}
                  Enable 2FA
                </button>
                <button type="button" onClick={() => setSetupData(null)} className="rounded-sm border border-neutral-300 px-4 py-2.5 text-base font-semibold text-neutral-700 hover:bg-neutral-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Disable flow */}
        {enabled && (
          <form onSubmit={disable} className="mt-6 rounded-sm border border-neutral-200 bg-white p-6" data-testid="twofa-disable-panel">
            <p className="font-heading text-lg font-bold text-[#0A0A0A]">Disable 2FA</p>
            <p className="mt-1 text-sm text-neutral-600">Enter a code from your authenticator app (or a backup code) to turn off 2FA.</p>
            <input
              data-testid="twofa-disable-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              placeholder="000000 or backup code"
              className={`mt-3 ${CODE_INPUT_CLASS}`}
            />
            <button
              type="submit"
              data-testid="twofa-confirm-disable"
              disabled={busy || !disableCode}
              className="mt-4 flex items-center gap-2 rounded-sm border border-[#DC2626] px-4 py-2.5 text-base font-semibold text-[#DC2626] transition-all duration-200 hover:bg-[#DC2626]/5 disabled:opacity-60"
            >
              {busy && <CircleNotch size={18} className="animate-spin" />}
              Disable two-factor authentication
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}
